import { useEffect, useRef } from 'react';
import { useUser } from '@/queries/useUser';
import { QUERY_KEYS, stanNotify } from '@/lib';
import { useQueryClient } from '@tanstack/react-query';
import type { RunningJob } from '@/types/user';

// Job type identifiers (matches backend job types from Vue)
export const JOB_TYPES = {
    IMPORT_CONTACTS: 'import-contacts',
    GRANT_PRODUCT_ACCESS: 'grant-product-access',
} as const;

// Polling interval while job is in progress (10 seconds, matching Vue)
const POLL_INTERVAL_MS = 10000;

// Max polling iterations (60 = 10 minutes, matching Vue)
const MAX_POLL_COUNT = 60;

export interface JobProgress {
    /** Whether a job of this type is currently running */
    isRunning: boolean;
    /** The active job, if any */
    activeJob: (RunningJob & { progress?: number }) | null;
    /** Progress percentage (0-100) */
    progress: number;
}

export interface ImportProgress extends JobProgress {
    /** Alias for isRunning (backwards compatibility) */
    isImporting: boolean;
    /** Refresh the import status */
    refresh: () => void;
}

/**
 * Hook to track CSV contact import progress using the user's running_jobs.
 *
 * Automatically polls for updates while an import is in progress.
 * Shows success notification when import completes.
 * Returns information about the current import status.
 */
export function useImportProgress(): ImportProgress {
    const { user, refetchUser } = useUser();
    const queryClient = useQueryClient();
    const pollCountRef = useRef(0);
    const wasImportingRef = useRef(false);

    // Find any active import job (Vue checks if job exists, no status check)
    const activeJob =
        (user?.running_jobs?.find((job: RunningJob) => job.type === JOB_TYPES.IMPORT_CONTACTS) as
            | (RunningJob & { progress?: number })
            | null) ?? null;

    const isRunning = !!activeJob;

    // Progress is directly on the job object (not job.data.progress)
    const progress = activeJob?.progress ?? 0;

    // Poll for updates while import is in progress
    useEffect(() => {
        if (!isRunning) {
            // Reset poll count when not running
            pollCountRef.current = 0;
            return;
        }

        const intervalId = setInterval(() => {
            pollCountRef.current++;

            // Stop polling after max iterations (10 minutes)
            if (pollCountRef.current > MAX_POLL_COUNT) {
                clearInterval(intervalId);
                return;
            }

            refetchUser();
        }, POLL_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, [isRunning, refetchUser]);

    // Show success notification when import completes (matching Vue watcher)
    useEffect(() => {
        // If was importing and now not importing = import completed
        if (wasImportingRef.current && !isRunning) {
            // Refresh customer data
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AUDIENCE] });

            stanNotify({
                type: 'success',
                title: 'Success',
                text: 'Your contacts have been imported.',
                duration: 5000,
            });
        }

        wasImportingRef.current = isRunning;
    }, [isRunning, queryClient]);

    return {
        isRunning,
        activeJob,
        progress,
        isImporting: isRunning,
        refresh: refetchUser,
    };
}

/**
 * Hook to track product grant progress using the user's running_jobs.
 * Similar to useImportProgress but for product grant operations.
 */
export function useProductGrantProgress(): JobProgress {
    const { user } = useUser();

    // Find any active product grant job
    const activeJob =
        (user?.running_jobs?.find((job: RunningJob) => job.type === JOB_TYPES.GRANT_PRODUCT_ACCESS) as
            | (RunningJob & { progress?: number })
            | null) ?? null;

    const isRunning = !!activeJob;
    const progress = activeJob?.progress ?? 0;

    return {
        isRunning,
        activeJob,
        progress,
    };
}
